// src/pages/ExamVerify.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';

export default function ExamVerify(){
    const { id } = useParams(); // attemptId
    const nav = useNavigate();

    // UI
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    // Kamera
    const videoRef  = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const [previewOn, setPreviewOn] = useState(false);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState(localStorage.getItem('exam.cameraId') || '');

    // Snapshot
    const [snapshot, setSnapshot] = useState(null); // { blob, url, mime, name }

    // Detekcja twarzy (native → fallback TFJS BlazeFace)
    const [detectorReady, setDetectorReady] = useState(false);
    const [usingNative, setUsingNative]     = useState(false);
    const nativeDetectorRef = useRef(null);
    const blazeModelRef     = useRef(null);
    const detectTimerRef    = useRef(null);

    const [faceOk, setFaceOk]       = useState(false);
    const [faceHint, setFaceHint]   = useState('Ustaw twarz w owalu i spójrz w kamerę.');

    const [needManual, setNeedManual]       = useState(false);
    const [manualConsent, setManualConsent] = useState(false);
    const manualSelfieKeyRef = useRef('');

    // ========= Helpers =========
    function pickValidDeviceId(savedId, vids) {
        const ids = new Set(vids.map(d => d.deviceId));
        return ids.has(savedId) ? savedId : (vids[0]?.deviceId || '');
    }
    function stopPreview(keepState=false){
        try { streamRef.current?.getTracks()?.forEach(t=>t.stop()); } catch {}
        if (videoRef.current) videoRef.current.srcObject = null;
        streamRef.current = null;
        if (!keepState) setPreviewOn(false);
    }
    function clearDetectTimer(){
        if (detectTimerRef.current){
            clearTimeout(detectTimerRef.current);
            detectTimerRef.current = null;
        }
    }
    function ensureScript(src, globalName){
        return new Promise((resolve, reject) => {
            if (globalName && window[globalName]) return resolve();
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing){
                const done = () => resolve();
                const fail = () => reject(new Error(`Load fail: ${src}`));
                existing.addEventListener('load', done, { once:true });
                existing.addEventListener('error', fail, { once:true });
                // jeśli już się załadował, rozwiąż od razu
                if (existing.getAttribute('data-loaded') === '1') return resolve();
                return;
            }
            const s = document.createElement('script');
            s.src = src; s.async = true;
            s.onload = () => { s.setAttribute('data-loaded','1'); resolve(); };
            s.onerror = () => reject(new Error(`Load fail: ${src}`));
            document.head.appendChild(s);
        });
    }

    // ========= Kamera: auto-start na wejściu =========
    useEffect(()=>{
        (async ()=>{
            try{
                if (!navigator.mediaDevices?.getUserMedia) {
                    setErr('Twoja przeglądarka nie obsługuje kamery (getUserMedia).');
                    return;
                }
                // HTTPS poza localhost
                const host = window.location.hostname;
                const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
                if (!window.isSecureContext && !isLocalhost) {
                    setErr('Wymagany jest HTTPS (poza localhost) do użycia kamery.');
                    return;
                }

                const all  = await navigator.mediaDevices.enumerateDevices();
                const vids = all.filter(d => d.kind === 'videoinput');
                setDevices(vids);
                const chosenId = pickValidDeviceId(deviceId || localStorage.getItem('exam.cameraId') || '', vids);
                if (chosenId !== deviceId) setDeviceId(chosenId);

                stopPreview(true);

                const base = { width: { ideal: 1280 }, height: { ideal: 720 } };
                let constraints = chosenId
                    ? { video: { ...base, deviceId: { exact: chosenId } }, audio:false }
                    : { video: { ...base, facingMode: { ideal:'user' } }, audio:false };

                let stream;
                try{
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                }catch(e){
                    if (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError'){
                        localStorage.removeItem('exam.cameraId');
                        setDeviceId('');
                        constraints = { video: { ...base, facingMode: { ideal:'user' } }, audio:false };
                        stream = await navigator.mediaDevices.getUserMedia(constraints);
                    } else { throw e; }
                }

                streamRef.current = stream;
                const v = videoRef.current;
                if (v){
                    v.srcObject = stream;
                    v.muted = true; v.playsInline = true;
                    await new Promise((resolve)=>{
                        const ready = () => {
                            v.removeEventListener('loadedmetadata', ready);
                            const p = v.play();
                            if (p && typeof p.then === 'function') p.then(()=>resolve()).catch(()=>resolve());
                            else resolve();
                        };
                        if (v.readyState >= 1) ready(); else v.addEventListener('loadedmetadata', ready, { once:true });
                    });
                }
                // zapisz faktycznie użyte deviceId
                const usedId = stream.getVideoTracks()[0]?.getSettings?.().deviceId || chosenId || '';
                if (usedId){
                    localStorage.setItem('exam.cameraId', usedId);
                    if (usedId !== deviceId) setDeviceId(usedId);
                }

                setPreviewOn(true);
                setErr('');
            }catch(e){
                setPreviewOn(false);
                const name = e?.name || 'Error';
                const message = e?.message || '';
                setErr(`Brak dostępu do kamery (${name}: ${message}).`);
            }
        })();

        return ()=>{ stopPreview(); clearDetectTimer(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Restart podglądu, gdy ręcznie zmienisz urządzenie
    useEffect(()=>{
        (async ()=>{
            if (!previewOn) return;
            try{
                const all  = await navigator.mediaDevices.enumerateDevices();
                const vids = all.filter(d => d.kind === 'videoinput');
                const chosenId = pickValidDeviceId(deviceId, vids);
                if (chosenId !== deviceId) setDeviceId(chosenId);

                stopPreview(true);

                const base = { width: { ideal: 1280 }, height: { ideal: 720 } };
                let constraints = chosenId
                    ? { video: { ...base, deviceId: { exact: chosenId } }, audio:false }
                    : { video: { ...base, facingMode: { ideal:'user' } }, audio:false };

                let stream;
                try{
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                }catch(e){
                    if (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError'){
                        localStorage.removeItem('exam.cameraId');
                        setDeviceId('');
                        constraints = { video: { ...base, facingMode: { ideal:'user' } }, audio:false };
                        stream = await navigator.mediaDevices.getUserMedia(constraints);
                    } else { throw e; }
                }

                streamRef.current = stream;
                const v = videoRef.current;
                if (v){
                    v.srcObject = stream;
                    v.muted = true; v.playsInline = true;
                    await v.play().catch(()=>{});
                }
                const usedId = stream.getVideoTracks()[0]?.getSettings?.().deviceId || chosenId || '';
                if (usedId){
                    localStorage.setItem('exam.cameraId', usedId);
                    if (usedId !== deviceId) setDeviceId(usedId);
                }

                setPreviewOn(true);
            }catch{}
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    // ========= Ładowanie detektora (native → TFJS BlazeFace) =========
    useEffect(()=>{
        let cancelled = false;
        async function loadDetector(){
            // FaceDetector (native)
            if ('FaceDetector' in window){
                try{
                    nativeDetectorRef.current = new window.FaceDetector({ fastMode:true, maxDetectedFaces:1 });
                    setUsingNative(true);
                    if (!cancelled) setDetectorReady(true);
                    return;
                }catch{/* fallback */}
            }
            // TFJS BlazeFace (CDN)
            try{
                await ensureScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.14.0/dist/tf.min.js', 'tf');
                await ensureScript('https://cdn.jsdelivr.net/npm/tensorflow-blazeface-sync@0.1.3/dist/blazeface.min.js', 'blazeface');
                // @ts-ignore
                const model = await window.blazeface.load();
                blazeModelRef.current = model;
                setUsingNative(false);
                if (!cancelled) setDetectorReady(true);
            }catch(e){
                console.error('Błąd ładowania detektora:', e);
                if (!cancelled) setDetectorReady(false);
            }
        }
        if (!snapshot) loadDetector();
        return ()=>{ cancelled = true; };
    }, [snapshot]);

    // ========= Pętla wykrywania twarzy + wskazówki + ZIELONY OWAL =========
    useEffect(()=>{
        if (snapshot){ clearDetectTimer(); return; }
        if (!detectorReady){ setFaceOk(false); setFaceHint('Ładuję wykrywanie twarzy…'); return; }

        let cancelled = false;
        const off = document.createElement('canvas');
        const octx = off.getContext('2d');

        const tick = async () => {
            if (cancelled) return;
            const v = videoRef.current;
            if (!v || v.readyState < 2) return schedule();

            try{
                const vw = v.videoWidth || 0, vh = v.videoHeight || 0;
                let faces = [];
                if (usingNative && nativeDetectorRef.current){
                    faces = await nativeDetectorRef.current.detect(v);
                    faces = (faces||[]).map(f => ({
                        x: f.boundingBox.x, y: f.boundingBox.y, width: f.boundingBox.width, height: f.boundingBox.height
                    }));
                } else if (blazeModelRef.current){
                    // @ts-ignore
                    const preds = await blazeModelRef.current.estimateFaces(v, false);
                    faces = (preds||[]).map(p => {
                        const [x1,y1] = p.topLeft; const [x2,y2] = p.bottomRight;
                        return { x:x1, y:y1, width:x2-x1, height:y2-y1 };
                    });
                }

                if (!faces || faces.length === 0){
                    setFaceOk(false);
                    setFaceHint('Nie widzę twarzy. Ustaw się przodem i spójrz w kamerę.');
                } else if (faces.length > 1){
                    setFaceOk(false);
                    setFaceHint('W kadrze jest więcej niż jedna twarz. Zostań sam/a w kadrze.');
                } else {
                    const box = faces[0];
                    const cx = box.x + box.width/2;
                    const cy = box.y + box.height/2;

                    // Owal jak w overlay: ellipse 40% 55% at 50% 45%
                    const rx = vw * 0.40, ry = vh * 0.55;
                    const ox = vw * 0.50, oy = vh * 0.45;

                    const norm = Math.pow((cx-ox)/rx,2) + Math.pow((cy-oy)/ry,2);
                    const centered = norm <= 1;

                    const relH = box.height / vh;
                    const tooSmall = relH < 0.25;
                    const tooBig   = relH > 0.5;

                    // prosta heurystyka jasności
                    let brightEnough = true;
                    try{
                        const sampleW = 160, sampleH = 160;
                        off.width = sampleW; off.height = sampleH;
                        octx.drawImage(v, ox - sampleW/2, oy - sampleH/2, sampleW, sampleH, 0, 0, sampleW, sampleH);
                        const data = octx.getImageData(0,0,sampleW,sampleH).data;
                        let sum = 0;
                        for (let i=0;i<data.length;i+=4){
                            sum += 0.2126*data[i] + 0.7152*data[i+1] + 0.0722*data[i+2];
                        }
                        const avg = sum / (sampleW*sampleH);
                        brightEnough = avg >= 55;
                    }catch{ brightEnough = true; }

                    if (!centered){
                        setFaceOk(false); setFaceHint('Wyśrodkuj twarz w owalu.');
                    } else if (tooSmall){
                        setFaceOk(false); setFaceHint('Przybliż twarz do kamery.');
                    } else if (tooBig){
                        setFaceOk(false); setFaceHint('Oddal się od kamery.');
                    } else if (!brightEnough){
                        setFaceOk(false); setFaceHint('Lepsze oświetlenie: spójrz w stronę światła.');
                    } else {
                        setFaceOk(true); setFaceHint('Super! Twarz wykryta. Możesz zrobić zdjęcie.');
                    }
                }
            }catch{ /* ignoruj pojedyncze błędy */ }
            finally { schedule(); }
        };

        const schedule = ()=>{ clearDetectTimer(); detectTimerRef.current = setTimeout(tick, 130); };
        schedule();
        return ()=>{ cancelled = true; clearDetectTimer(); };
    }, [detectorReady, usingNative, snapshot]);

    // ========= Foto / upload / verify =========
    function retake(){
        if (snapshot?.url) URL.revokeObjectURL(snapshot.url);
        setSnapshot(null);
        setFaceOk(false);
        setNeedManual(false);
        setManualConsent(false);
        manualSelfieKeyRef.current = '';
    }

    function takePhoto(){
        const v = videoRef.current, c = canvasRef.current;
        if (!v || !c) return;

        const targetW=720, targetH=960;
        c.width=targetW; c.height=targetH;
        const ctx = c.getContext('2d');

        const vw=v.videoWidth, vh=v.videoHeight;
        if (!vw || !vh) return;

        const scale = Math.max(targetW/vw, targetH/vh);
        const drawW = vw*scale, drawH = vh*scale;
        const dx = (targetW-drawW)/2, dy=(targetH-drawH)/2;

        ctx.fillStyle='#fff'; ctx.fillRect(0,0,targetW,targetH);
        ctx.drawImage(v, dx, dy, drawW, drawH);

        c.toBlob((blob)=>{
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            setSnapshot({ blob, url, mime:'image/jpeg', name:'verify-selfie.jpg' });
        }, 'image/jpeg', 0.9);
    }

    async function uploadAndVerify(){
        if (!snapshot?.blob){ setErr('Najpierw zrób zdjęcie.'); return; }
        setBusy(true); setErr(''); setMsg('Wysyłam zdjęcie…');
        try{
            // 1) presign
            const init = await API.verifyInit(id, { filename: snapshot.name, contentType: snapshot.mime, sizeBytes: snapshot.blob.size });
            // 2) PUT
            const up = await fetch(init.url, { method: init.method || 'PUT', headers: init.headers || {}, body: snapshot.blob });
            if (!up.ok){
                const t = await up.text().catch(()=>'(brak treści)');
                throw new Error(`Upload nieudany: ${up.status} ${t}`);
            }
            // 3) auto verify
            setMsg('Weryfikuję…');
            const auto = await API.verifyAuto(id, init.key);
            const matched = auto?.matched ?? auto?.match ?? auto?.ok;
            const score   = auto?.score;

            if (matched){
                setMsg(`Weryfikacja OK${typeof score==='number' ? ` (score ${score.toFixed?.(3) ?? score})` : ''}.`);
                nav(`/student/egzamin/${id}/trwa`);
                return;
            }

            manualSelfieKeyRef.current = init.key;
            setNeedManual(true);
            setMsg('Automatyczna weryfikacja nie powiodła się. Możesz przesłać zdjęcie do weryfikacji manualnej i kontynuować.');
        }catch(e){
            setErr(e.message || 'Błąd weryfikacji');
        }finally{ setBusy(false); }
    }

    async function sendManualAndContinue(){
        if (!manualSelfieKeyRef.current){ setErr('Brak zdjęcia do weryfikacji manualnej.'); return; }
        if (!manualConsent){ setErr('Zaznacz zgodę na przekazanie zdjęcia do weryfikacji manualnej.'); return; }
        setBusy(true); setErr(''); setMsg('Zgłaszam do weryfikacji manualnej…');
        try{
            const r = await API.verifyManual(id, manualSelfieKeyRef.current);
            if (!r?.ok) throw new Error('Zgłoszenie nie powiodło się');
            setMsg('Zgłoszono do manualnej weryfikacji — możesz kontynuować egzamin.');
            nav(`/student/egzamin/${id}/trwa`);
        }catch(e){
            setErr(e.message || 'Błąd zgłoszenia do weryfikacji manualnej');
        }finally{ setBusy(false); }
    }

    // ========= UI =========
    return (
        <div style={{ maxWidth: 900,  padding: '0 16px', marginTop:80 , display:'grid', gap:16 }}>
            <h1>Weryfikacja tożsamości</h1>

            {/* Wskazówki jak w rejestracji */}
            {!snapshot && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        padding:'10px 14px',
                        borderRadius:10,
                        background: faceOk ? '#e7f6ec' : '#fff7e6',
                        color:'#333'
                    }}
                >
                    {detectorReady ? faceHint : 'Ładuję moduł wykrywania twarzy… (działa we wszystkich nowoczesnych przeglądarkach)'}
                </div>
            )}

            <div data-tour="face-oval" style={{ display:'grid', gap:12, gridTemplateColumns:'1fr auto', alignItems:'start' }}>
                {/* Podgląd + owal */}
                <div style={{ position:'relative', width:'100%', maxWidth:420, aspectRatio:'3/4', background:'#000', borderRadius:12, overflow:'hidden' }}>
                    {!snapshot ? (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{ width:'100%', height:'100%', objectFit:'cover', transform:'scaleX(-1)' }}
                            />
                            {/* MASKA (przyciemnienie poza owale) */}
                            <div
                                style={{
                                    position:'absolute', inset:0, pointerEvents:'none',
                                    background:'radial-gradient(ellipse 50% 55% at 50% 45%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 61%)'
                                }}
                            />
                            {/* OBRYS OWALU: szary → zielony gdy faceOk */}
                            <svg
                                style={{ position:'absolute', inset:0, pointerEvents:'none' }}
                                viewBox="0 0 100 100" preserveAspectRatio="none"
                            >
                                <ellipse
                                    cx="50" cy="60" rx="30" ry="45"
                                    fill="none"
                                    stroke={faceOk ? '#22c55e' : 'rgba(255,255,255,0.75)'}
                                    strokeWidth="2.5"
                                />
                            </svg>
                        </>
                    ) : (
                        <img src={snapshot.url} alt="Podgląd" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    )}
                </div>

                {/* Sterowanie kamerą / urządzenie */}
                {!snapshot && (
                    <div style={{ display:'grid', gap:8 }}>
                        <label style={{ fontSize:14 }}>Kamera</label>
                        <select
                            value={deviceId}
                            onChange={e=>setDeviceId(e.target.value)}
                            style={{ padding:'6px 8px', borderRadius:8 }}
                        >
                            {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Kamera'}</option>)}
                        </select>
                        <button className={'but-smal'}
                            onClick={async ()=>{
                                // „przeładuj podgląd” — zadziała również gdy przeglądarka dopiero co nadała uprawnienia
                                const all  = await navigator.mediaDevices.enumerateDevices();
                                const vids = all.filter(x => x.kind === 'videoinput');
                                setDevices(vids);
                                const chosenId = pickValidDeviceId(deviceId, vids);
                                if (chosenId !== deviceId) setDeviceId(chosenId);
                            }}
                        >
                            Odśwież listę
                        </button>
                    </div>
                )}
            </div>

            <canvas ref={canvasRef} style={{ display:'none' }} />

            {/* Przyciski akcji */}
            {!snapshot ? (
                <div data-tour="snap"  style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button className={'but-cta'} onClick={takePhoto} disabled={!previewOn || !detectorReady || !faceOk}>
                        {(!detectorReady || !faceOk) ? 'Czekam na poprawne ustawienie twarzy…' : 'Zrób zdjęcie'}
                    </button>
                    <small style={{ opacity:0.8 }}>
                        Po prawidłowym ułożeniu twarzy należy zrobić zdjęcie i przesłać w celu weryfikacji
                    </small>
                </div>
            ) : (
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button className={'but-cta'} onClick={uploadAndVerify} disabled={busy}>{busy ? 'Wysyłam…' : 'Wyślij i zweryfikuj'}</button>
                </div>
            )}

            {/* Manualna weryfikacja po niepowodzeniu auto */}
            {needManual && (
                <div style={{ border:'2px solid #d9a', background:'#fff6fb', borderRadius:12, padding:16 }}>
                    <h3 style={{ marginTop:0 }}>Automatyczna weryfikacja nie powiodła się</h3>
                    <p>
                        Możesz przekazać zdjęcie do <b>weryfikacji manualnej</b> przez prowadzącego i <b>kontynuować egzamin</b>.
                        Jeżeli prowadzący odrzuci weryfikację, egzamin może zostać unieważniony.
                    </p>
                    <label style={{ display:'flex', alignItems:'center', gap:8, margin:'8px 0' }}>
                        <input type="checkbox" checked={manualConsent} onChange={e=>setManualConsent(e.target.checked)} />
                        Wyrażam zgodę na przekazanie zdjęcia do weryfikacji manualnej.
                    </label>
                    <button onClick={sendManualAndContinue} disabled={!manualConsent || busy}>
                        Wyślij do weryfikacji manualnej i kontynuuj
                    </button>
                </div>
            )}

            {(msg || err) && (
                <div style={{ padding:12, borderRadius:8, background: err ? '#fee' : '#f6ffed', color: err ? '#a00' : '#063' }}>
                    {err || msg}
                </div>
            )}
        </div>
    );
}
